import { get } from 'dot-prop';
import { sortBy } from 'lodash';
import { BaseModel } from 'storm-react-diagrams';
import { channelTypeOptions } from '../../../../const';
import {
  BaseLinkModel,
  BaseNodeModel,
  ConditionNodeModel
} from '../../../../models';
import {
  ChangeItemChangeset,
  ChangeItemChangesetWithItems,
  ChangesetItemType,
  ConfigView,
  ConfigViewType,
  DesignArgument,
  DesignChangeMap,
  DesignChannelType,
  DesignDialogNode,
  DesignGlobalIntent,
  DesignInput,
  DesignMap,
  DesignModuleLocalVariable,
  DesignOutput,
  GenericDesignChangeItem,
  ItemListConfigViewView
} from '../../../../types';
import { Case, convertCase } from '../../../functions';
import NodeRegistry from '../../../NodeRegistry';
import {
  designCompoundIntentLabels,
  changesetItemsLabels
} from '../data';
import { BaseChangeItemConflict, ChangesetRow } from '../types';
import { getChangeItemObject } from './get-change-item-object';

const excludedEndsWithProps =[
  'lastLocalChange',
  'lastSavedChange',
  'author'
].join('|');

const excludedSearch = new RegExp(
  `(?:\\.|^)(${excludedEndsWithProps})$`
);

function stringify(value: unknown): string {
  if (value == null) {
    return '';
  } else if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return value?.toString() ?? '<Not Found>';
}

type ItemListLabels = Record<ItemListConfigViewView, Record<string, string>>;

const getItemListLabels = (): ItemListLabels => {
  const formLabels = window.l.form;

  return {
    [ItemListConfigViewView.argument]: {
      argumentName: formLabels.argument_name,
      type: formLabels.input_type,
      value: formLabels.argument_value,
      varName: formLabels.scoped_variable_name
    },
    [ItemListConfigViewView.customHeader]: {
      argumentName: 'Name',
      id: 'Variable Id',
      value: 'Value',
      varName: 'Variable Name'
    },
    [ItemListConfigViewView.dynamicVariableReference]: {
      defaultValue: formLabels.default_value,
      sensitive: formLabels.sensitive,
      speakAs: formLabels.speak_as
    },
    [ItemListConfigViewView.examplePhrases]: {
      selection: 'Phrase'
    },
    [ItemListConfigViewView.keyValue]: {
      key: formLabels.key,
      value: formLabels.value
    },
    [ItemListConfigViewView.localIntent]: {
      selection: 'Intent'
    },
    // TODO: It looks like 'moduleInputs' view is unused.
    [ItemListConfigViewView.moduleInputs]: {},
    [ItemListConfigViewView.moduleReturn]: {
      argumentName: formLabels.module_argument_name,
      type: formLabels.variable_type,
      varName: formLabels.return_value_to
    },
    [ItemListConfigViewView.replyButton]: {
      intent: 'Intent'
    },
    [ItemListConfigViewView.return]: {
      type: formLabels.variable_type,
      varName: formLabels.scoped_variable_name
    },
    [ItemListConfigViewView.routerArgument]: {
      argumentName: formLabels.column_to_match,
      ignoreCase: formLabels.ignore_case,
      type: formLabels.match_with,
      value: formLabels.argument_value,
      varName: formLabels.scoped_variable_name
    },
    [ItemListConfigViewView.routerReturn]: {
      argumentName: formLabels.column_to_return,
      type: formLabels.variable_type,
      varName: formLabels.scoped_variable_name
    },
    [ItemListConfigViewView.variableReference]: {
      defaultValue: formLabels.default_value,
      sensitive: formLabels.sensitive,
      speakAs: formLabels.speak_as
    }
  };
};

interface GetChangesetRowLabelProps {
  configView: ConfigView;
  key: string;
}

function getChangesetRowLabel({
  configView,
  key
}: GetChangesetRowLabelProps): React.ReactNode | null {
  const splitKeys = key.split('.');
  const defaultLabel = convertCase(
    splitKeys.join('.').replace(/^props.config/, ''),
    Case.title
  );
  if (!configView) {
    return  defaultLabel;
  } else if (configView.type === ConfigViewType.itemList) {
    const itemNumber = parseInt(splitKeys[splitKeys.length - 2]) + 1;
    if (isNaN(itemNumber)) {
      return null;
    }

    const subLabel = getItemListLabels()[configView.view][
      splitKeys[splitKeys.length - 1]
    ];
    return `${configView.label} ${itemNumber} (${subLabel})`;
  } else if (configView.type === ConfigViewType.promptTextarea) {
    const channelLabel = channelTypeOptions.find(({ value }) => {
      return value === (configView.channelLayer ?? DesignChannelType.voice);
    })?.name;
    const languageLabel = window.apm.getLanguage(configView.lang)?.name;
    return `${channelLabel} (${languageLabel})`;
  } else if ('label' in configView) {
    return configView.label;
  }

  return defaultLabel;
}

interface GetChangesetRowProps {
  changeset: ChangeItemChangeset;
  conflictItem: BaseChangeItemConflict;
  configViews: Map<number | string, any>;
  source: GenericDesignChangeItem;
  sourceDesignMap: DesignMap;
  target: GenericDesignChangeItem;
  targetDesignMap: DesignMap;
}

function getChangesetRow({
  changeset,
  conflictItem,
  configViews,
  source,
  sourceDesignMap,
  target,
  targetDesignMap
}: GetChangesetRowProps): ChangesetRow | null {
  const key = ('key' in changeset) ? changeset.key.join('.') : null;
  if (key && excludedSearch.test(key)) {
    return null;
  }

  const sourceObject = getChangeItemObject(source);
  const targetObject = getChangeItemObject(target);
  if (
    changeset.type == ChangesetItemType.overwrite
    || !key
  ) {
    return {
      id: '',
      label: '',
      source: stringify(sourceObject),
      target: stringify(targetObject)
    };
  }

  let label: React.ReactNode;
  let sourceLabel: string;
  let targetLabel: string;
  const itemSection = conflictItem.key.split('.')[0] as keyof(DesignChangeMap);
  if (itemSection === 'compoundIntents') {
    const typedKey = key as keyof(typeof designCompoundIntentLabels);
    label = designCompoundIntentLabels[typedKey];

    // Try to get intent names instead of IDs.
    const sourceId = stringify(get(sourceObject, key));
    const sourceInent = sourceDesignMap.intents.get(sourceId);
    sourceLabel = (changeset.type === ChangesetItemType.del) ? '' : (
      sourceInent?.name ?? sourceId
    );
    const targetId = stringify(get(targetObject, key));
    const targetInent = targetDesignMap.intents.get(targetId);
    targetLabel = targetInent?.name ?? targetId;
  } else if (itemSection === 'globalIntents') {
    const firstKey = changeset.key[0];
    const lastKey = changeset.key[changeset.key.length - 1];
    if (firstKey === 'conditionProperties') {
      const conditionProperties = (sourceObject as DesignGlobalIntent)[firstKey];
      const model = NodeRegistry.buildNode(conditionProperties);
      const configViews = (model as ConditionNodeModel).getConfigViews();
      const configView = configViews.find((cv) => cv.key === lastKey);
      label = getChangesetRowLabel({
        configView,
        key: key.slice(firstKey.length + 1)
      });
    } else if (firstKey === 'intent') {
      label = 'Intent';
      // Try to get intent names instead of IDs.
      const sourceId = stringify(get(sourceObject, key));
      const sourceInent = sourceDesignMap.intents.get(sourceId);
      sourceLabel = (changeset.type === ChangesetItemType.del) ? '' : (
        sourceInent?.name ?? sourceId
      );
      const targetId = stringify(get(targetObject, key));
      const targetInent = targetDesignMap.intents.get(targetId);
      targetLabel = targetInent?.name ?? targetId;
    } else if (firstKey === 'module') {
      label = 'Module';
      // Try to get module names instead of IDs.
      const sourceId = stringify(get(sourceObject, key));
      const sourceInent = sourceDesignMap.modules.get(sourceId);
      sourceLabel = (changeset.type === ChangesetItemType.del) ? '' : (
        sourceInent?.name ?? sourceId
      );
      const targetId = stringify(get(targetObject, key));
      const targetInent = targetDesignMap.modules.get(targetId);
      targetLabel = targetInent?.name ?? targetId;
    }
    sourceLabel = (changeset.type === ChangesetItemType.del) ? '' : (
      stringify(get(sourceObject, key))
    );
    targetLabel = stringify(get(targetObject, key));
  } else {
    // Skip past `props.config` to get the config key.
    const configView = (
      // Try to get the specific key.
      configViews.get(changeset.key.slice(2).join('.'))
      // Fallback to the generic key.
      ?? configViews.get(changeset.key[2])
    );
    label = getChangesetRowLabel({ configView, key });
    sourceLabel = (changeset.type === ChangesetItemType.del) ? '' : (
      stringify(get(sourceObject, key))
    );
    targetLabel = stringify(get(targetObject, key));
  }

  return {
    id: key,
    label,
    source: sourceLabel,
    target: targetLabel
  };
}

interface GetChangesetItemsChangesProps {
  sourceChange: ChangeItemChangesetWithItems | null;
  targetChange: ChangeItemChangesetWithItems | null;
}

function getChangesetItemsChanges({
  sourceChange,
  targetChange
}: GetChangesetItemsChangesProps): ChangesetRow[] {
  const parentKeys = sourceChange?.key ?? targetChange?.key ?? [];
  const parentKey = parentKeys.join('.');
  const sourceItems = sourceChange?.items ?? {};
  const targetItems = targetChange?.items ?? {};
  const itemIds = new Set([
    ...Object.keys(sourceItems),
    ...Object.keys(targetItems)
  ]);

  const changesetRows: ChangesetRow[] = [];
  itemIds.forEach((itemId) => {
    const sourceItemChange = sourceItems[itemId];
    const targetItemChange = targetItems[itemId];
    const itemKey = parentKeys[parentKeys.length - 1];
    let label: string;

    switch (itemKey) {
      case 'arguments':
      case 'inputs':
      case 'outputs': {
        const sourceItem = sourceItemChange?.item as (
          DesignArgument
          | DesignInput
          | DesignOutput
          | null
        );
        const targetItem = targetItemChange?.item as typeof sourceItem;
        const firstItem = sourceItem ?? targetItem as Exclude<typeof sourceItem, null>;

        const itemsLabel = changesetItemsLabels[itemKey] ?? itemKey;
        const itemLabel = (
          firstItem.varName
          ?? firstItem.argumentName
          ?? '<unnamed>'
        );
        label = `${itemsLabel} (${itemLabel})`;
        break;
      }
      case 'localVariables': {
        const sourceItem = sourceItemChange?.item as DesignModuleLocalVariable | null;
        const targetItem = targetItemChange?.item as DesignModuleLocalVariable | null;
        const firstItem = sourceItem ?? targetItem as DesignModuleLocalVariable;
        label = `Local Variable (${firstItem.name})`;
        break;
      }
      case 'nodes': {
        const sourceItem = sourceItemChange?.item as DesignDialogNode | null;
        const targetItem = targetItemChange?.item as DesignDialogNode | null;
        const firstItem = sourceItem ?? targetItem as DesignDialogNode;
        label = `Node (${firstItem.text})`;
        break;
      }
      default:
        console.error('Unable to find parent key for item change', sourceChange, targetChange);
        label = 'Unknown';
        break;
    }

    let parentLabel = '';
    // This handles stuff like `props.config.functions.outputs`.
    if (parentKeys.length > 3) {
      parentLabel = (
        parentKeys[2][0].toUpperCase()
        + parentKeys[2].slice(1)
      );
    }

    changesetRows.push({
      id: `${parentKey}.${itemId}`,
      label: [parentLabel, label].filter((x) => !!x).join(' '),
      source: `<Source ${sourceItemChange?.action ?? 'none'}>`,
      target: `<Target ${targetItemChange?.action ?? 'none'}>`
    });
  });

  return changesetRows;
}

export interface GetConflictItemChangesProps {
  conflictItem: BaseChangeItemConflict;
  model: BaseModel<any> | null;
  source: GenericDesignChangeItem;
  sourceDesignMap: DesignMap;
  target: GenericDesignChangeItem;
  targetDesignMap: DesignMap;
}

export function getConflictItemChanges({
  conflictItem,
  model,
  source,
  sourceDesignMap,
  target,
  targetDesignMap
}: GetConflictItemChangesProps): ChangesetRow[] {
  const changesetKeys = new Set(
    (source.changeset ?? [])?.concat(target.changeset ?? []).map((change) => {
      return ('key' in change) ? (
        change.key.join('.')
      ) : '';
    })
  );
  if (!changesetKeys.size) {
    changesetKeys.add('');
  }

  const configViews = new Map<number | string, any>();
  if (
    model instanceof BaseNodeModel
    || model instanceof BaseLinkModel
  ) {
    model.getConfigViews().forEach((configView) => {
      if (!configView || !('key' in configView)) {
        return;
      }

      const key = ('lang' in configView) ? (
        `${configView.key}.${configView.lang}`
      ) : configView.key;
      if (configViews.has(key)) {
        console.error(
          `Key '${key}' is set multiple times in config views.`,
          {
            oldView: configViews.get(key),
            newView: configView
          }
        );
      }

      configViews.set(key, configView);
    });
  }

  const changesetRows: (ChangesetRow | null)[] = [];
  changesetKeys.forEach((changesetKey) => {
    const sourceChange = (!changesetKey) ? { type: ChangesetItemType.overwrite } : (
      source.changeset?.find((change) => 'key' in change && change.key.join('.') === changesetKey)
    );
    const targetChange = (!changesetKey) ? { type: ChangesetItemType.overwrite } : (
      target.changeset?.find((change) => 'key' in change && change.key.join('.') === changesetKey)
    );
    const sourceChangeHasItems = (sourceChange && 'items' in sourceChange);
    const targetChangeHasItems = (targetChange && 'items' in targetChange);

    if (sourceChangeHasItems || targetChangeHasItems) {
      getChangesetItemsChanges({
        sourceChange: sourceChange as ChangeItemChangesetWithItems | null,
        targetChange: targetChange as ChangeItemChangesetWithItems | null
      }).forEach((row) => changesetRows.push(row));
      return;
    }

    changesetRows.push(getChangesetRow({
      changeset: (sourceChange ?? targetChange) as ChangeItemChangeset,
      configViews,
      conflictItem,
      source,
      sourceDesignMap,
      target,
      targetDesignMap
    }));
  });

  const finalChangesetRows = changesetRows.filter((row) => {
    return (
      row != null
      && row.label != null
      && row.source !== row.target
    );
  }) as ChangesetRow[];

  return sortBy(finalChangesetRows, (changeset) => changeset.label);
}
